import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Building2, Users, Award, Factory, TrendingUp, Globe, ArrowRight, Package, ChevronLeft, ChevronRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ShowcaseCategoryCard from "@/components/ShowcaseCategoryCard";
import AccessRequestModal from "@/components/AccessRequestModal";
import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { getImageUrl } from "@/utils/imageUrl";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default showcase categories (used when database is empty)
const DEFAULT_SHOWCASE_CATEGORIES = [
  { id: "fsu", name: "Floor Standing Units (FSU)", slug: "fsu", image: "", display_order: 0 },
  { id: "ctu", name: "Counter Top Units (CTU)", slug: "ctu", image: "", display_order: 1 },
  { id: "wall-mount", name: "Wall Mount Units", slug: "wall-mount", image: "", display_order: 2 },
  { id: "display-bins", name: "Display Bins & Distribution Units", slug: "display-bins", image: "", display_order: 3 },
  { id: "standees", name: "Standees", slug: "standees", image: "", display_order: 4 },
  { id: "creative", name: "Creative Units", slug: "creative", image: "", display_order: 5 },
  { id: "kiosks", name: "Premium Kiosks", slug: "kiosks", image: "", display_order: 6 },
  { id: "hangers", name: "Hangers & Danglers", slug: "hangers", image: "", display_order: 7 },
];

// Client logos sprite
const LOGOS_SPRITE_URL = "https://customer-assets.emergentagent.com/job_52f1121e-b9de-4fec-a879-a87485a0115a/artifacts/0r18z12t_image_2026-01-27_141509827.png";

// Carousel Block Component with Slider Support and Responsive Grid
function CarouselBlock({ block, style, paddingClass, textAlignClass, bgStyle }) {
  // Responsive settings with fallbacks
  const mobileRows = block.mobileRows || 1;
  const mobileCols = block.mobileCols || 2;
  const tabletRows = block.tabletRows || 2;
  const tabletCols = block.tabletCols || 3;
  const desktopRows = block.desktopRows || block.carouselRows || 2;
  const desktopCols = block.desktopCols || block.carouselCols || 4;
  
  const images = block.images || [];
  const enableSlider = block.enableSlider || false;
  
  // Use desktop settings for pagination calculation (largest grid)
  const imagesPerPage = desktopRows * desktopCols;
  const totalPages = Math.ceil(images.length / imagesPerPage);
  
  const [currentPage, setCurrentPage] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(enableSlider);
  const intervalRef = useRef(null);

  // Auto-slide functionality
  useEffect(() => {
    if (enableSlider && isAutoPlaying && totalPages > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentPage(prev => (prev + 1) % totalPages);
      }, 4000); // Slide every 4 seconds
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [enableSlider, isAutoPlaying, totalPages]);

  const goToPage = (page) => {
    setCurrentPage(page);
    setIsAutoPlaying(false); // Pause auto-play on manual navigation
    // Resume auto-play after 10 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const nextPage = () => goToPage((currentPage + 1) % totalPages);
  const prevPage = () => goToPage((currentPage - 1 + totalPages) % totalPages);

  // Get images for current page
  const startIdx = currentPage * imagesPerPage;
  const visibleImages = enableSlider 
    ? images.slice(startIdx, startIdx + imagesPerPage)
    : images.slice(0, imagesPerPage);

  return (
    <section className={paddingClass} style={bgStyle}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {block.title && (
          <div className={`mb-8 ${textAlignClass}`}>
            <h2 
              className={`${style.fontSize || 'text-3xl'} font-bold tracking-tight mb-4`}
              style={{ fontFamily: 'Manrope' }}
            >
              {block.title}
            </h2>
            {block.subtitle && (
              <p className="text-lg text-muted-foreground">{block.subtitle}</p>
            )}
          </div>
        )}

        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Arrows - Only show if slider enabled and more than one page */}
          {enableSlider && totalPages > 1 && (
            <>
              <button
                onClick={prevPage}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all hover:scale-110"
                aria-label="Previous"
              >
                <ChevronLeft className="h-6 w-6 text-gray-800" />
              </button>
              <button
                onClick={nextPage}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all hover:scale-110"
                aria-label="Next"
              >
                <ChevronRight className="h-6 w-6 text-gray-800" />
              </button>
            </>
          )}

          {/* Responsive Image Grid - Using CSS custom properties for responsive columns */}
          <div 
            className="grid gap-3 md:gap-4 transition-opacity duration-500"
            style={{
              '--mobile-cols': mobileCols,
              '--tablet-cols': tabletCols,
              '--desktop-cols': desktopCols,
              gridTemplateColumns: `repeat(var(--mobile-cols), 1fr)`,
            }}
          >
            <style>{`
              @media (min-width: 768px) {
                [style*="--tablet-cols"] {
                  grid-template-columns: repeat(var(--tablet-cols), 1fr) !important;
                }
              }
              @media (min-width: 1024px) {
                [style*="--desktop-cols"] {
                  grid-template-columns: repeat(var(--desktop-cols), 1fr) !important;
                }
              }
            `}</style>
            {visibleImages.map((img, idx) => (
              <div 
                key={img.id || `${currentPage}-${idx}`} 
                className="relative group overflow-hidden rounded-lg border bg-gray-100 transition-all duration-300 hover:shadow-xl"
              >
                <img 
                  src={getImageUrl(img.url)} 
                  alt={img.alt || img.caption || `Image ${startIdx + idx + 1}`}
                  className="w-full aspect-square object-contain transition-transform duration-300 group-hover:scale-105"
                />
                {img.caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-2 text-sm text-center">
                    {img.caption}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Page Indicators - Only show if slider enabled and more than one page */}
          {enableSlider && totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              {Array.from({ length: totalPages }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToPage(idx)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    idx === currentPage 
                      ? 'bg-primary scale-125' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to page ${idx + 1}`}
                />
              ))}
              <span className="ml-4 text-sm text-muted-foreground">
                {currentPage + 1} / {totalPages} ({images.length} images)
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// Dynamic Block Renderer Component
function DynamicBlock({ block }) {
  if (!block.visible) return null;

  const style = block.style || {};
  const bgStyle = {
    backgroundColor: style.backgroundColor || '#ffffff',
    color: style.fontColor || '#000000'
  };

  // Parse padding class to actual padding
  const paddingClass = style.padding || 'py-12';
  const textAlignClass = style.textAlign === 'center' ? 'text-center' : 
                         style.textAlign === 'right' ? 'text-right' : 'text-left';

  switch (block.type) {
    case 'hero':
      return (
        <section 
          className={`relative bg-cover bg-center min-h-[60vh] flex items-center ${paddingClass}`}
          style={{
            backgroundImage: block.image 
              ? `linear-gradient(rgba(15, 23, 42, 0.85), rgba(15, 23, 42, 0.85)), url('${getImageUrl(block.image)}')`
              : `linear-gradient(135deg, ${style.backgroundColor || '#0f172a'} 0%, #1e293b 100%)`
          }}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 w-full">
            <div className={`max-w-3xl ${textAlignClass === 'text-center' ? 'mx-auto' : ''}`}>
              <h1 
                className={`${style.fontSize || 'text-5xl'} md:text-6xl font-bold tracking-tight mb-6`}
                style={{ fontFamily: 'Manrope', color: style.fontColor || '#ffffff' }}
              >
                {block.title}
              </h1>
              {block.subtitle && (
                <p className="text-xl leading-relaxed mb-8" style={{ color: style.fontColor ? `${style.fontColor}cc` : '#d1d5db' }}>
                  {block.subtitle}
                </p>
              )}
              {block.content && (
                <p className="text-lg leading-relaxed mb-8" style={{ color: style.fontColor ? `${style.fontColor}99` : '#9ca3af' }}>
                  {block.content}
                </p>
              )}
              {block.buttonText && (
                block.buttonLink === '#access-request' ? (
                  <Button 
                    size="lg" 
                    className="gap-2 h-12 px-8"
                    onClick={() => window.dispatchEvent(new CustomEvent('openAccessRequest'))}
                  >
                    {block.buttonText}
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                ) : (
                  <Link to={block.buttonLink || '/catalog'}>
                    <Button size="lg" className="gap-2 h-12 px-8">
                      {block.buttonText}
                      <ArrowRight className="h-5 w-5" />
                    </Button>
                  </Link>
                )
              )}
            </div>
          </div>
        </section>
      );

    case 'text':
      return (
        <section className={paddingClass} style={bgStyle}>
          <div className={`max-w-4xl mx-auto px-6 md:px-12 lg:px-24 ${textAlignClass}`}>
            {block.title && (
              <h2 
                className={`${style.fontSize || 'text-3xl'} font-bold tracking-tight mb-4`}
                style={{ fontFamily: 'Manrope' }}
              >
                {block.title}
              </h2>
            )}
            {block.subtitle && (
              <p className="text-lg text-muted-foreground mb-6">{block.subtitle}</p>
            )}
            {block.content && (
              <p className="text-lg leading-relaxed">{block.content}</p>
            )}
          </div>
        </section>
      );

    case 'image':
      return (
        <section className={paddingClass} style={bgStyle}>
          <div className={`max-w-7xl mx-auto px-6 md:px-12 lg:px-24 ${textAlignClass}`}>
            {block.title && (
              <h2 
                className={`${style.fontSize || 'text-3xl'} font-bold tracking-tight mb-4`}
                style={{ fontFamily: 'Manrope' }}
              >
                {block.title}
              </h2>
            )}
            {block.subtitle && (
              <p className="text-lg text-muted-foreground mb-8">{block.subtitle}</p>
            )}
            {block.image && (
              <img 
                src={getImageUrl(block.image)} 
                alt={block.title || 'Image'} 
                className="w-full max-w-4xl mx-auto rounded-lg shadow-lg object-contain"
              />
            )}
          </div>
        </section>
      );

    case 'carousel':
      return <CarouselBlock block={block} style={style} paddingClass={paddingClass} textAlignClass={textAlignClass} bgStyle={bgStyle} />;

    case 'cta':
      return (
        <section 
          className={paddingClass}
          style={{ 
            backgroundColor: style.backgroundColor || '#3a29a6',
            color: style.fontColor || '#ffffff'
          }}
        >
          <div className={`max-w-4xl mx-auto px-6 md:px-12 lg:px-24 ${textAlignClass}`}>
            {block.title && (
              <h2 
                className={`${style.fontSize || 'text-3xl'} font-bold tracking-tight mb-6`}
                style={{ fontFamily: 'Manrope' }}
              >
                {block.title}
              </h2>
            )}
            {block.subtitle && (
              <p className="text-xl mb-8 leading-relaxed opacity-90">{block.subtitle}</p>
            )}
            {block.content && (
              <p className="text-lg mb-8 leading-relaxed opacity-80">{block.content}</p>
            )}
            {block.buttonText && (
              block.buttonLink === '#access-request' ? (
                <Button 
                  size="lg" 
                  variant="secondary" 
                  className="gap-2 h-12 px-8"
                  onClick={() => window.dispatchEvent(new CustomEvent('openAccessRequest'))}
                >
                  {block.buttonText}
                  <ArrowRight className="h-5 w-5" />
                </Button>
              ) : (
                <Link to={block.buttonLink || '/catalog'}>
                  <Button size="lg" variant="secondary" className="gap-2 h-12 px-8">
                    {block.buttonText}
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              )
            )}
          </div>
        </section>
      );

    case 'stats':
      let statsData = [];
      try {
        statsData = JSON.parse(block.content || '[]');
      } catch (e) {
        statsData = [];
      }
      
      return (
        <section className={paddingClass} style={bgStyle}>
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            {block.title && (
              <div className={`mb-12 ${textAlignClass}`}>
                <h2 
                  className={`${style.fontSize || 'text-3xl'} font-bold tracking-tight mb-4`}
                  style={{ fontFamily: 'Manrope' }}
                >
                  {block.title}
                </h2>
                {block.subtitle && (
                  <p className="text-lg text-muted-foreground">{block.subtitle}</p>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {statsData.map((stat, index) => (
                <div key={index} className="text-center p-6 bg-white/50 rounded-lg border">
                  <div className="text-4xl font-bold mb-2 text-primary" style={{ fontFamily: 'Manrope' }}>
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground uppercase tracking-wider">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'features':
      let featuresData = [];
      try {
        featuresData = JSON.parse(block.content || '[]');
      } catch (e) {
        featuresData = [];
      }
      
      return (
        <section className={paddingClass} style={bgStyle}>
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            {block.title && (
              <div className={`mb-12 ${textAlignClass}`}>
                <h2 
                  className={`${style.fontSize || 'text-3xl'} font-bold tracking-tight mb-4`}
                  style={{ fontFamily: 'Manrope' }}
                >
                  {block.title}
                </h2>
                {block.subtitle && (
                  <p className="text-lg text-muted-foreground">{block.subtitle}</p>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuresData.map((feature, index) => (
                <div key={index} className="p-6 bg-white rounded-lg border hover:border-primary/50 transition-colors">
                  <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Manrope' }}>
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    default:
      return null;
  }
}

export default function LandingPage() {
  const [companyInfo, setCompanyInfo] = useState(null);
  const [showcaseCategories, setShowcaseCategories] = useState([]);
  const [customBlocks, setCustomBlocks] = useState([]);
  const [useCustomBlocks, setUseCustomBlocks] = useState(false);
  const [showAccessRequestModal, setShowAccessRequestModal] = useState(false);

  useEffect(() => {
    fetchCompanyInfo();
    fetchShowcaseCategories();
    fetchCustomBlocks();

    // Listen for access request modal open event
    const handleOpenAccessRequest = () => setShowAccessRequestModal(true);
    window.addEventListener('openAccessRequest', handleOpenAccessRequest);
    return () => window.removeEventListener('openAccessRequest', handleOpenAccessRequest);
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`${API}/company-info`);
      setCompanyInfo(response.data);
    } catch (error) {
      console.error("Error fetching company info:", error);
    }
  };

  const fetchShowcaseCategories = async () => {
    try {
      const response = await axios.get(`${API}/showcase/categories`);
      setShowcaseCategories(response.data.length > 0 ? response.data : DEFAULT_SHOWCASE_CATEGORIES);
    } catch (error) {
      console.error("Error fetching showcase categories:", error);
      setShowcaseCategories(DEFAULT_SHOWCASE_CATEGORIES);
    }
  };

  const fetchCustomBlocks = async () => {
    try {
      const response = await axios.get(`${API}/page/home`);
      const blocks = response.data.blocks || [];
      if (blocks.length > 0) {
        // Sort by order and filter visible blocks
        const sortedBlocks = blocks
          .filter(b => b.visible !== false)
          .sort((a, b) => a.order - b.order);
        setCustomBlocks(sortedBlocks);
        setUseCustomBlocks(true);
      }
    } catch (error) {
      console.error("Error fetching custom blocks:", error);
    }
  };

  const stats = companyInfo ? [
    { icon: Award, label: "Years of Excellence", value: `${companyInfo.years_experience}+` },
    { icon: TrendingUp, label: "Projects Completed", value: `${companyInfo.projects_completed}+` },
    { icon: Globe, label: "Cities Covered", value: `${companyInfo.cities_covered}+` },
    { icon: Users, label: "Enterprise Clients", value: `${companyInfo.clients_count}+` },
  ] : [];

  // If custom blocks exist, render them along with showcase categories
  if (useCustomBlocks && customBlocks.length > 0) {
    // Separate CTA blocks from other blocks
    const ctaBlocks = customBlocks.filter(block => block.type === 'cta');
    const nonCtaBlocks = customBlocks.filter(block => block.type !== 'cta');
    
    return (
      <div className="min-h-screen">
        <Navbar />

        {/* Render Non-CTA Custom Blocks */}
        {nonCtaBlocks.map((block) => (
          <DynamicBlock key={block.id} block={block} />
        ))}

        {/* Product Categories Section - Always show */}
        <section id="product-categories" className="py-20 bg-muted/30">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
                Product Categories
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Browse our comprehensive range of retail display solutions
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="product-categories-grid">
              {showcaseCategories.map((category) => (
                <ShowcaseCategoryCard key={category.id} category={category} />
              ))}
            </div>
          </div>
        </section>

        {/* Client Logos Section */}
        <section className="py-16 bg-white border-y">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Manrope' }}>
                Trusted by Industry Leaders
              </h2>
              <p className="text-muted-foreground">Powering retail presence for India's biggest brands</p>
            </div>
            <div className="flex justify-center" data-testid="client-logos-grid">
              <img 
                src={LOGOS_SPRITE_URL} 
                alt="Trusted by leading brands including Coca-Cola, P&G, Dabur, ITC, Airtel, Gillette, and 60+ more" 
                className="w-full max-w-5xl h-auto object-contain"
              />
            </div>
          </div>
        </section>

        {/* Manufacturing Excellence Section */}
        <section id="capabilities" className="py-20 md:py-32">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
                Manufacturing Excellence
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                State-of-the-art facility equipped with German automated machinery, delivering precision and scale.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="p-8 border border-border/50 rounded-sm bg-white hover:border-primary/50 transition-colors" data-testid="capability-card-1">
                <Factory className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-2xl font-semibold mb-3" style={{ fontFamily: 'Manrope' }}>42,000+ Sq. Ft. Facility</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Spacious manufacturing unit designed for high-volume production and quality control.
                </p>
              </div>

              <div className="p-8 border border-border/50 rounded-sm bg-white hover:border-primary/50 transition-colors" data-testid="capability-card-2">
                <Building2 className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-2xl font-semibold mb-3" style={{ fontFamily: 'Manrope' }}>10+ German Machines</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Advanced automated machinery ensuring precision, consistency, and rapid turnaround.
                </p>
              </div>

              <div className="p-8 border border-border/50 rounded-sm bg-white hover:border-primary/50 transition-colors" data-testid="capability-card-3">
                <Award className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-2xl font-semibold mb-3" style={{ fontFamily: 'Manrope' }}>ISO Certified Quality</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Rigorous quality standards meeting international benchmarks for enterprise clients.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Render CTA Blocks at the End */}
        {ctaBlocks.map((block) => (
          <DynamicBlock key={block.id} block={block} />
        ))}

        <Footer />

        {/* Access Request Modal */}
        <AccessRequestModal 
          isOpen={showAccessRequestModal} 
          onClose={() => setShowAccessRequestModal(false)} 
        />
      </div>
    );
  }

  // Default layout when no custom blocks
  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero Section */}
      <section 
        className="hero-section relative bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.85), rgba(15, 23, 42, 0.85)), url('https://images.unsplash.com/photo-1767294274634-613a3545e36d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBpbmR1c3RyaWFsJTIwZmFjdG9yeSUyMGludGVyaW9yJTIwY2xlYW58ZW58MHx8fHwxNzY5MTY0MDUzfDA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-32">
          <div className="max-w-3xl">
            <div className="inline-block mb-6">
              <span className="text-xs font-bold tracking-widest uppercase text-secondary px-4 py-2 bg-secondary/10 rounded-sm border border-secondary/30">
                Enterprise B2B Solutions
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'Manrope' }} data-testid="hero-heading">
              Revolutionizing Retail with Premium POSM Solutions
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              26+ years of manufacturing excellence. Trusted by India's largest FMCG and retail brands for high-quality Floor Standing Units, Counter Top Displays, and custom retail solutions.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/login">
                <Button size="lg" className="gap-2 bg-primary hover:bg-primary/90 h-12 px-8" data-testid="hero-cta-btn">
                  Access Catalog
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
              <a href="#capabilities">
                <Button size="lg" variant="outline" className="h-12 px-8 border-white text-white hover:bg-white/10">
                  Learn More
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={index} className="stat-card p-6 bg-muted/30 border border-border/50 rounded-sm" data-testid={`stat-card-${index}`}>
                  <IconComponent className="h-10 w-10 text-primary mb-3" />
                  <div className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>{stat.value}</div>
                  <div className="text-sm text-muted-foreground uppercase tracking-wider">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section id="capabilities" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
              Manufacturing Excellence
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              State-of-the-art facility equipped with German automated machinery, delivering precision and scale.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 border border-border/50 rounded-sm bg-white hover:border-primary/50 transition-colors" data-testid="capability-card-1">
              <Factory className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-2xl font-semibold mb-3" style={{ fontFamily: 'Manrope' }}>42,000+ Sq. Ft. Facility</h3>
              <p className="text-muted-foreground leading-relaxed">
                Spacious manufacturing unit designed for high-volume production and quality control.
              </p>
            </div>

            <div className="p-8 border border-border/50 rounded-sm bg-white hover:border-primary/50 transition-colors" data-testid="capability-card-2">
              <Building2 className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-2xl font-semibold mb-3" style={{ fontFamily: 'Manrope' }}>10+ German Machines</h3>
              <p className="text-muted-foreground leading-relaxed">
                Advanced automated machinery ensuring precision, consistency, and rapid turnaround.
              </p>
            </div>

            <div className="p-8 border border-border/50 rounded-sm bg-white hover:border-primary/50 transition-colors" data-testid="capability-card-3">
              <Award className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-2xl font-semibold mb-3" style={{ fontFamily: 'Manrope' }}>ISO Certified Quality</h3>
              <p className="text-muted-foreground leading-relaxed">
                Rigorous quality standards meeting international benchmarks for enterprise clients.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories Section */}
      <section id="product-categories" className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
              Product Categories
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Browse our comprehensive range of retail display solutions
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="product-categories-grid">
            {showcaseCategories.map((category) => (
              <ShowcaseCategoryCard key={category.id} category={category} />
            ))}
          </div>
        </div>
      </section>

      {/* Client Logos Section */}
      <section className="py-16 bg-white border-y">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Manrope' }}>
              Trusted by Industry Leaders
            </h2>
            <p className="text-muted-foreground">Powering retail presence for India's biggest brands</p>
          </div>
          <div className="flex justify-center" data-testid="client-logos-grid">
            <img 
              src={LOGOS_SPRITE_URL} 
              alt="Trusted by leading brands including Coca-Cola, P&G, Dabur, ITC, Airtel, Gillette, and 60+ more" 
              className="w-full max-w-5xl h-auto object-contain"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope' }}>
            Ready to Transform Your Retail Presence?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Access our comprehensive product catalog and request custom quotes for your business needs.
          </p>
          <Link to="/login">
            <Button size="lg" variant="secondary" className="gap-2 h-12 px-8" data-testid="cta-login-btn">
              Client Login
              <ArrowRight className="h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
